import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { BodySecondary, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { fetchBankAccountsIfNeeded } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getBankAccounts,
    getBankAccountsLoaded,
    getBankAccountsLoading,
} from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getFees } from '@/redux/modules/processingFee';
import { PaymentMethods } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import BankAccountOnFile from '@liveauctioneers/caterwaul-components/lib/BankAccountOnFile/BankAccountOnFile';
import BankAccountSelection from '@/components/PaymentMethodSelection/BankAccountSelection';
import LaPlaidLink from '@/components/Plaid/LaPlaidLink';
import PaymentMethod from './PaymentMethod';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

type Props = {
    active: boolean;
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency: string;
    onBankAccountSelectionChange: (bankAccount: BankAccount) => void;
    onPaymentMethodChange: (paymentMethod: PaymentMethods) => void;
    paymentMethod: PaymentMethods;
    requireACHForCheckout: boolean;
    selectedBankAccount: BankAccount;
};

const PaymentMethodACH = ({
    active,
    bidderPaysProcessingFee,
    catalogId,
    currency,
    onBankAccountSelectionChange,
    onPaymentMethodChange,
    paymentMethod,
    requireACHForCheckout,
    selectedBankAccount,
}: Props) => {
    const dispatch = useAppDispatch();

    const bankAccountsLoaded = useAppSelector(getBankAccountsLoaded);
    const bankAccounts = useAppSelector(getBankAccounts);
    const bankAccountsLoading = useAppSelector(getBankAccountsLoading);

    const processingFees = useAppSelector((state) => getFees(state, catalogId));

    const processingFeeDifference = Boolean(processingFees) ? processingFees.ccFee - processingFees.achFee : 0;

    useEffect(() => {
        if (!Boolean(bankAccountsLoaded)) {
            dispatch(fetchBankAccountsIfNeeded());
        }
    }, [dispatch, bankAccountsLoaded]);

    const selected = paymentMethod === PaymentMethods.Ach;

    if (!active) {
        if (selected) {
            return selectedBankAccount && <BankAccountOnFile bankAccount={selectedBankAccount} />;
        } else {
            return null;
        }
    }

    return (
        <>
            <PaymentMethod
                data-testid="payment-method-ach"
                onClick={() => onPaymentMethodChange(PaymentMethods.Ach)}
                selected={selected}
                subtitle={
                    (!requireACHForCheckout && processingFeeDifference) > 0 &&
                    bidderPaysProcessingFee && (
                        <BodySecondary color="secondary">
                            <FormattedMessage
                                id="checkout.payment.achFees"
                                values={{
                                    processingFee: (
                                        <FormattedNumber
                                            currency={currency}
                                            key="checkout-payment-ach-fees-processing-fee"
                                            style="currency"
                                            value={processingFeeDifference / 100}
                                        />
                                    ),
                                }}
                            />
                        </BodySecondary>
                    )
                }
                title={
                    <LabelStrong>
                        <FormattedMessage id="checkout.payment.ach" />
                    </LabelStrong>
                }
            >
                {bankAccountsLoading ? (
                    <Throbber testid="payment-method-ach-loading" />
                ) : (
                    <BankAccountSelection
                        bankAccounts={bankAccounts}
                        selectBankAccount={(bankAccount: BankAccount) => {
                            onBankAccountSelectionChange(bankAccount);
                        }}
                    />
                )}
                <StyledAddNewPaymentMethod>
                    <LaPlaidLink useTextStyle />
                </StyledAddNewPaymentMethod>
            </PaymentMethod>
        </>
    );
};

export default PaymentMethodACH;

const StyledAddNewPaymentMethod = styled.div`
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        margin-bottom: 32px;
    }
`;
